import { useEffect, useSyncExternalStore } from "react";
import { auth } from "../../firebase/auth.js";

/**
 * Module-level cache for a whole Firestore collection, shared by every component.
 * The first subscriber triggers one load; concurrent callers share the in-flight request;
 * refresh() (after writes) reloads once and updates every subscribed screen.
 */
export function createCollectionStore(fetchFn, { errorMessage = "Unable to load data." } = {}) {
  let state = { data: [], loading: true, error: null, loaded: false };
  let inflight = null;
  const listeners = new Set();

  const emit = (patch) => {
    state = { ...state, ...patch };
    listeners.forEach((l) => l());
  };

  const load = ({ force = false } = {}) => {
    // A forced refresh after a write must not reuse a request that started before the write
    if (inflight) return force ? inflight.catch(() => {}).then(() => load({ force: true })) : inflight;
    if (state.loaded && !force) return Promise.resolve(state.data);

    if (!state.loaded) emit({ loading: true, error: null });
    inflight = fetchFn()
      .then((data) => {
        emit({ data, loading: false, error: null, loaded: true });
        return data;
      })
      .catch((err) => {
        console.error(errorMessage, err);
        emit({ loading: false, error: errorMessage });
        throw err;
      })
      .finally(() => {
        inflight = null;
      });
    return inflight;
  };

  const reset = () => {
    inflight = null;
    emit({ data: [], loading: true, error: null, loaded: false });
  };

  // Never keep one user's data around after sign-out / account switch
  let lastUid;
  auth.onAuthStateChanged((user) => {
    const uid = user?.uid || null;
    if (lastUid !== undefined && uid !== lastUid) reset();
    lastUid = uid;
  });

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };
  const getSnapshot = () => state;

  /** React hook: current { data, loading, error } and auto-load on first use. */
  const useStore = () => {
    const snapshot = useSyncExternalStore(subscribe, getSnapshot);
    useEffect(() => {
      load().catch(() => {});
    }, [snapshot.loaded]);
    return snapshot;
  };

  return {
    useStore,
    load,
    refresh: () => load({ force: true }).catch(() => state.data),
    reset
  };
}

export default createCollectionStore;
